import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateID } from '../lib/util';
import * as types from '../types';
import { Species } from '../types/gql-op-types';
import {
  recalculateGiftStatus,
  baseCartReducers,
  couponCartReducers,
  giftCardRedemptionCartReducers,
  giftShippingAddressCartReducers,
  referralCodeCartReducers,
} from './baseCart';

interface IAddSingleLineCartItem {
  sku: string;
  quantity?: number;
  currency: types.FiCurrency;
}

interface IAddBundleCartItem extends IAddSingleLineCartItem {
  subscriptionSku: string;
  addonSkus?: string[];
  quantity?: number;
  isGift?: boolean;
  species?: Species;
}

interface IAddGiftCardCartItem extends IAddSingleLineCartItem {
  giftCardDelivery: types.GiftCardDelivery;
}

interface IUpdateCartItemQuantity {
  id: string;
  quantity: number;
}

interface IResetCart {
  resetCoupon?: boolean;
}

export const cartInitialState: types.StoreCart = {
  cartItems: {},
  currency: types.FiCurrency.USD,
  containsAllGiftItems: false,
  containsGiftItems: false,
};

const name = 'storeCart' as const;
export const { actions: cartActions, reducer: cartReducer } = createSlice({
  name,
  initialState: cartInitialState,
  reducers: {
    ...baseCartReducers,
    ...couponCartReducers,
    ...giftCardRedemptionCartReducers,
    ...referralCodeCartReducers,
    ...giftShippingAddressCartReducers,
    addSingleLineCartItem(cart: types.StoreCart, { payload }: PayloadAction<IAddSingleLineCartItem>) {
      const singleLineCartItem: types.SingleLineCartItem = {
        cartItemId: generateID(),
        lineItem: {
          lineItemId: generateID(),
          sku: payload.sku,
        },
        // Use quantity if it was provided or fallback to quantity of '1'
        quantity: payload.quantity && payload.quantity > 0 ? payload.quantity : 1,
      };

      return recalculateGiftStatus({
        ...cart,
        cartItems: {
          ...cart.cartItems,
          [singleLineCartItem.cartItemId]: singleLineCartItem,
        },
        currency: payload.currency,
      });
    },
    addBundleCartItem(cart: types.StoreCart, { payload }: PayloadAction<IAddBundleCartItem>) {
      const bundleCartItem: types.BundleCartItem = {
        cartItemId: generateID(),
        lineItem: {
          lineItemId: generateID(),
          sku: payload.sku,
        },
        subscriptionLineItem: {
          lineItemId: generateID(),
          sku: payload.subscriptionSku,
        },
        addonLineItems: payload.addonSkus?.map((sku) => ({
          lineItemId: generateID(),
          sku,
        })),
        // Use quantity if it was provided or fallback to quantity of '1'
        quantity: payload.quantity && payload.quantity > 0 ? payload.quantity : 1,
        isGift: payload.isGift || false,
        species: payload.species,
      };

      return recalculateGiftStatus({
        ...cart,
        cartItems: {
          ...cart.cartItems,
          [bundleCartItem.cartItemId]: bundleCartItem,
        },
        currency: payload.currency,
      });
    },
    addGiftCardCartItem(cart: types.StoreCart, { payload }: PayloadAction<IAddGiftCardCartItem>) {
      const giftCardCartItem: types.GiftCardCartItem = {
        cartItemId: generateID(),
        lineItem: {
          lineItemId: generateID(),
          sku: payload.sku,
        },
        giftCardDelivery: payload.giftCardDelivery,

        quantity: 1,
      };

      return recalculateGiftStatus({
        ...cart,
        cartItems: {
          ...cart.cartItems,
          [giftCardCartItem.cartItemId]: giftCardCartItem,
        },
        currency: payload.currency,
      });
    },
    updateCartItemQuantity(cart: types.StoreCart, { payload }: PayloadAction<IUpdateCartItemQuantity>) {
      if (cart.cartItems[payload.id]) {
        cart.cartItems[payload.id].quantity = payload.quantity;
      }
    },

    resetCart(cart: types.StoreCart, { payload }: PayloadAction<IResetCart>) {
      let newState = {
        ...cartInitialState,
        // Currency should be sticky when resetting.
        // Fallback to initial state for users with stale localStorage that lacks this field.
        currency: cart.currency ?? cartInitialState.currency,
      };
      if (!payload.resetCoupon) {
        const couponCode = cart.couponCode;
        newState = { ...newState, couponCode };
      }
      return newState;
    },
    setAwinClick(cart: types.StoreCart, { payload }: PayloadAction<string | undefined>) {
      cart.awinClick = payload;
    },
    setFacebookClick(cart: types.StoreCart, { payload }: PayloadAction<string | undefined>) {
      cart.facebookClick = payload;
    },
  },
});
